// ** Reactstrap
import {
    Badge,
    FormFeedback,
    Input,
    InputGroup,
    InputGroupText,
    Label
} from "reactstrap";
import { useContext, useEffect, useRef } from "react";
import get from "lodash/get";
import { Controller } from "react-hook-form";
import CustomTooltip from "../ui-components/tooltip";
import { ThemeContext } from "@context/theme";
import classnames from "classnames";
import size from "lodash/size";
import { useTranslation } from "react-i18next";

const InputField = (props) => {
    const {
        errors,
        label = "",
        name,
        type = "text",
        id,
        placeholder = "",
        autoFocus = false,
        disabled = false,
        control,
        className = "",
        tooltipContent = "",
        required = false,
        multiple = false,
        customOnChange = () => {},
        handleBlur = () => {},
        prefix = {},
        suffix = {},
        isDirtyField = false,
        showErrMsg = true,
        desc = null,
        loading = false,
        inputClassName = "",
        enableBlurEffect = true,
        onFocus = () => {},
        rows = 2
    } = props;
    const { t } = useTranslation();
    const message = get(errors, `${name}.message`) || get(errors, `${id}.message`);
    const isInvalid = Boolean(get(errors, name) || get(errors, id));

    /**
     * If there is an error message, return the error message
     * @returns message
     */
    const renderError = () => {
        return (
            message && (
                <FormFeedback data-cy={`error-${id}`}>
                    {t(message)}
                </FormFeedback>
            )
        );
    };

    const exceptThisSymbols = ["e", "E", "+", "-"];
    const { filterOpen } = useContext(ThemeContext);

    const inputRef = useRef();

    useEffect(() => {
        if (autoFocus) {
            setTimeout(() => {
                if (filterOpen) {
                    inputRef?.current?.focus();
                } else {
                    inputRef?.current?.blur();
                }
            }, 100);
        }
    }, [filterOpen]);
    
    const updatedSuffix = loading ? { icon: "loading mdi-spin" } : suffix;
    function removeLeadingInvisibleCharacters (str) {
        return str?.replace(/^[\p{Cf}\p{Z}]+/u, "");
    }
    
    return (
        <div className={classnames({
            "mb-1"     : !className,
            [className]: className
        })}
        data-tour={`input-${id}`}>
            {label && (
                <Label htmlFor={name} data-cy={`label-${id}`}>
                    {t(label)}
                    {required && (
                        <span className="text-danger fw-bolder">*</span>
                    )}
                    {Boolean(tooltipContent) && (
                        <>
                            <i
                                className="mdi mdi-information-outline ms-75"
                                id={`tooltip-${id}`}/>
                            <CustomTooltip
                                target={`tooltip-${id}`}
                                content={tooltipContent}/>
                        </>
                    )}
                </Label>
            )}
            <Controller
                id={name}
                name={name}
                control={control}
                render={({ field: { value, onChange } }) => (
                    <InputGroup
                        className={classnames({
                            "input-group-merge": prefix.txt || updatedSuffix,
                            "is-dirty-field"   : isDirtyField && !isInvalid
                        })}
                        id={`error-${name.replaceAll(".", "-")}`}>
                        {(prefix.txt || prefix.icon) && (
                            <InputGroupText
                                className={classnames({
                                    "border-danger": isInvalid
                                })}>
                                {t(prefix.txt)}
                                <i className={`mdi mdi-${prefix.icon}`} />
                            </InputGroupText>
                        )}
                        <Input
                            // @ts-ignore
                            onWheel={(e) => e.target.blur()}
                            innerRef={inputRef}
                            type={type}
                            placeholder={
                                placeholder ? t(placeholder) : `${t("Enter")} ${t(label)}`
                            }
                            id={name}
                            multiple={multiple}
                            rows={rows}
                            className={classnames("rounded", {
                                "border-0"                      : isDirtyField && !isInvalid,
                                [inputClassName]                : inputClassName,
                                rounded                         : !prefix.txt && !updatedSuffix,
                                "border-start-0 rounded-start-0": size(prefix)
                            })}
                            onChange={(e) => {
                                if (customOnChange) customOnChange(e.target.value);
                                const trimmedValue = removeLeadingInvisibleCharacters(e.target.value); // Remove leading spaces only
                                if (type === "number") {
                                    onChange(trimmedValue || null);
                                    return;
                                }
                                onChange(trimmedValue);
                            }}
                            onFocus={(e) => {
                                if (onFocus) onFocus(e);
                            }}
                            onBlur={(e) => {
                                if (enableBlurEffect) {
                                    if (handleBlur) {
                                        handleBlur();
                                    } else {
                                        if (customOnChange) customOnChange(e.target.value);
                                        onChange(e);
                                    }   
                                }
                            }}
                            min={0}
                            onKeyDown={(e) => {
                                if (type === "number") {
                                    if (exceptThisSymbols.includes(e.key)) e.preventDefault();
                                }
                            }}
                            value={value || value === 0 ? value : ""}
                            autoFocus={autoFocus}
                            disabled={disabled}
                            data-cy={`input-${id}`}
                            invalid={isInvalid}/>
                        {isInvalid && !showErrMsg && message && (
                            <CustomTooltip
                                target={`error-${name.replaceAll(".", "-")}`}
                                content={message}/>
                        )}
                        {(updatedSuffix.txt || updatedSuffix.icon) && (
                            <InputGroupText
                                className={classnames(
                                    "p-25 border-start-0",
                                    { "border-danger": isInvalid },
                                    { "border-0": isDirtyField && !isInvalid }
                                )}>
                                {updatedSuffix.txt ? <Badge color="light-primary">
                                    {t(updatedSuffix.txt)}
                                </Badge> : null}
                                <i className={`mdi mdi-${updatedSuffix.icon}`} />
                            </InputGroupText>
                        )}
                    </InputGroup>
                )}/>
            {showErrMsg && renderError()}
            {desc && (
                <p>
                    <small className="fw-bold" data-cy="input-desc">
                        <i>{t(desc)}</i>
                    </small>
                </p>
            )}
        </div>
    );
};

export default InputField;
